import {
  Badge,
  Body,
  FramedIcon,
  Icon,
  Stack,
  Sticker,
  isDualVariantIcon,
  useTheme,
} from "@biom3/react";
import { motion } from "motion/react";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import type { CampaignQuestState } from "../types";

type CampaignQuestItemProps = {
  quest: CampaignQuestState;
  onQuestClick: (quest: CampaignQuestState) => void;
  isWishlisted: boolean;
  isHighlighted: boolean;
  referralAccepted: boolean;
};

const getQuestIcon = (quest: CampaignQuestState) => {
  switch (quest.type) {
    case "campaign_referrer":
      return "People";
    case "in-game":
      return "ESports";
    case "join_discord_server":
      return "Discord";
    case "follow_x_account":
      return "X";
    case "post_x_tweet":
      return "X";
    case "retweet_and_like":
      return "X";
    case "join_telegram_chat":
      return "Telegram";
    case "no_validate":
    case "website":
      return "ApplicationWeb";
    case "social_engagement":
      return "SupportChat";
    case "app_store_image_validation":
      return "ImageAdd";
    case "trailer":
      return "Play";
    case "steam_wishlist":
      return "SteamStore";
    default:
      return "ESports";
  }
};

export const CampaignQuestItem = ({
  quest,
  onQuestClick,
  isWishlisted,
  isHighlighted,
  referralAccepted,
}: CampaignQuestItemProps) => {
  const pathname = usePathname();
  const suppressReferralBonus = pathname?.includes("/games/fates-exp");
  const { base } = useTheme();
  const disabled =
    (quest.completed &&
      !quest.refreshPeriodHours &&
      // keep campaign_referrer enabled even if completed
      quest.type !== "campaign_referrer") ||
    !isWishlisted ||
    (quest.type === "in_game" && !quest.linkUrl);

  const onClick = (e: { preventDefault: () => void }) => {
    onQuestClick(quest);
  };

  const questIcon = getQuestIcon(quest);

  const questItemSx = useMemo(
    () => ({
      bg: isHighlighted ? "base.color.translucent.emphasis.200" : "transparent",
      pointerEvents: disabled ? "none" : "auto",
      cursor: disabled ? "not-allowed" : "pointer",
      transitionDelay: base.motion.normal.gentle.cssDuration,
      padding: "base.spacing.x3",
      borderRadius: "base.borderRadius.x4",
      "&:hover": {
        background: isHighlighted
          ? "base.color.translucent.emphasis.400"
          : "base.color.fixed.white.100",
      },
      transition: base.motion.normal.fast.cssDuration,
    }),
    [base.motion, disabled, isHighlighted],
  );

  const fadedOutSx = useMemo(
    () => ({
      opacity:
        quest.completed &&
        !quest.refreshPeriodHours &&
        quest.type !== "campaign_referrer"
          ? 0.5
          : 1,
      transitionProperty: "opacity",
      transitionDuration: base.motion.normal.fast.cssDuration,
      transitionTimingFunction: base.motion.normal.gentle.cssEase,
      transitionDelay: base.motion.normal.gentle.cssDuration,
    }),
    [
      quest.completed,
      quest.refreshPeriodHours,
      quest.type,
      base.motion.normal.gentle,
      base.motion.normal.fast,
    ],
  );

  const completionCount = quest.completionCount && quest.completionCount > 0;
  const recurringBadgeRequired = !!(
    (quest.refreshPeriodHours && completionCount) ||
    (quest.type === "campaign_referrer" && completionCount)
  );
  const showAsCompleted =
    quest.type === "campaign_referrer"
      ? completionCount
      : (quest.completed && !recurringBadgeRequired) ||
        (recurringBadgeRequired && quest.completedToday);

  // Hide referral bonus on fates-exp
  const showReferralBonus =
    !suppressReferralBonus &&
    Boolean(quest.referralBonusPoints) &&
    Boolean(referralAccepted);
  const combinedPoints = showReferralBonus
    ? quest.campaignPoints + (quest.referralBonusPoints ?? 0)
    : quest.campaignPoints;
  const pointsRewardLabel = quest.categories?.includes("engagement_multiplier")
    ? "Bonus"
    : `+ ${new Intl.NumberFormat().format(combinedPoints)}`;

  const isNoRewardQuest = !(quest.campaignPoints > 0 || quest.gems > 0);
  const shouldShowBadge =
    !isNoRewardQuest || (isNoRewardQuest && showAsCompleted);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={"0"}
      sx={questItemSx}
      rc={
        (quest.type === "in_game" ||
          quest.type === "website" ||
          quest.type === "no_validate") &&
        quest.linkUrl ? (
          <motion.a
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noreferrer"
            onClick={onClick}
            href={quest.linkUrl ?? "#"}
            layout
            style={{
              textDecoration: "none",
              border: "none",
            }}
            exit={{ opacity: 0 }}
          />
        ) : (
          <motion.button
            onClick={onClick}
            disabled={disabled}
            layout
            style={{
              border: "none",
            }}
            exit={{ opacity: 0 }}
          />
        )
      }
    >
      <Sticker
        position={{ x: "right", y: "bottom" }}
        sx={{
          w: isHighlighted ? "base.icon.size.500" : "base.icon.size.400",
          h: isHighlighted ? "base.icon.size.500" : "base.icon.size.400",
        }}
      >
        <Sticker.FramedIcon
          icon="ImxRewards"
          variant="bold"
          emphasized
          sx={{
            w: "20px",
            h: "20px",
            fill: "base.color.fixed.black.1000",
            visibility: quest.perpetualRewardsEligible ? "visible" : "hidden",
            background: "base.gradient.1",
          }}
        />

        <FramedIcon
          {...(isDualVariantIcon(questIcon)
            ? { icon: questIcon }
            : { icon: questIcon })}
          sx={{
            ...fadedOutSx,
            w: isHighlighted ? "base.icon.size.500" : "base.icon.size.400",
            h: isHighlighted ? "base.icon.size.500" : "base.icon.size.400",
          }}
          emphasized
        />
      </Sticker>

      <Stack
        gap="2px"
        sx={{
          flex: 1,
          width: "100%",
          alignItems: "flex-start",
          pl: "base.spacing.x4",
        }}
      >
        <Body
          size="small"
          weight="bold"
          sx={{
            flex: 1,
            fontFamily: "base.font.family.body.primary",
            textAlign: "left",
            ...fadedOutSx,
            WebkitLineClamp: 10,
            transitionDelay: base.motion.normal.gentle.cssDuration,
            textDecoration:
              quest.completed &&
              !quest.refreshPeriodHours &&
              quest.type !== "campaign_referrer"
                ? "line-through"
                : "none",
          }}
        >
          {quest.name}
        </Body>
        {quest.participantCount &&
          !quest?.categories?.includes("engagement_multiplier") && (
            <Body
              size="small"
              weight="bold"
              sx={{
                flex: 1,
                fontFamily: "base.font.family.body.primary",
                textAlign: "left",
                WebkitLineClamp: 10,
                transitionDelay: base.motion.normal.gentle.cssDuration,
                opacity: 0.5,
              }}
            >
              {`${quest.participantCount} participants`}
            </Body>
          )}
        {quest.perpetualRewardsEligible && (
          <Body
            size="xSmall"
            sx={{
              flex: 1,
              color: "base.gradient.1",
              textAlign: "left",
            }}
          >
            Eligible for Perpetual Rewards
          </Body>
        )}
      </Stack>
      <Stack alignItems="flex-end" gap={"base.spacing.x1"}>
        <Stack direction="row" gap={"base.spacing.x1"}>
          {recurringBadgeRequired && (
            <Badge
              variant={showAsCompleted ? "success" : "dark"}
              sx={{ transitionDelay: base.motion.normal.gentle.cssDuration }}
              badgeContent={`x${quest.completionCount}`}
            />
          )}
          <Stack
            direction="column"
            alignItems="flex-end"
            gap={"base.spacing.x1"}
          >
            {shouldShowBadge && (
              <Badge
                variant={
                  showAsCompleted
                    ? "success"
                    : showReferralBonus
                      ? "emphasis"
                      : "dark"
                }
                sx={{
                  transitionDelay: base.motion.normal.gentle.cssDuration,
                  py: "base.spacing.x1",
                  px: "base.spacing.x2",
                  borderRadius: "base.borderRadius.x3",
                  width: "fit-content",
                }}
                badgeContent={
                  isNoRewardQuest && showAsCompleted ? (
                    <Icon variant="bold" icon="Tick" />
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      gap="base.spacing.x1"
                    >
                      <Body size={"small"}>{pointsRewardLabel}</Body>
                      <Icon variant="bold" icon="Sparkle" />
                    </Stack>
                  )
                }
              />
            )}
          </Stack>
        </Stack>
        {quest.gems > 0 && (
          <Stack direction="row" alignItems="center" gap={"base.spacing.x1"}>
            <Body
              weight="bold"
              sx={{
                color: "base.gradient.1",
                fontSize: isHighlighted ? "12px" : "10px",
                lineHeight: isHighlighted ? "12px" : "10px",
              }}
            >
              BONUS +{quest.gems}
            </Body>
            <Icon
              icon="ImxRewards"
              variant="bold"
              sx={{
                w: "base.icon.size.100",
                h: "base.icon.size.100",
                fill: "base.gradient.1",
              }}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
